import React, { useState } from 'react'
import styled from 'styled-components'
import SessionDialog from '../../sessions/components/SessionDialog'

const Field = styled.div`
  margin-bottom: 12px;
`
const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-bottom: 4px;
`
const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
`
const MoodRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`
const MoodPick = styled.button`
  appearance: none;
  border: 2px solid
    ${(p) => (p.$on ? p.theme?.color?.primary || '#1F3A5F' : 'transparent')};
  background: ${(p) =>
    p.$on ? p.theme?.color?.primarySoft || '#E8EEF6' : p.theme?.color?.bgSubtle || '#F4F0E9'};
  border-radius: 14px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 22px;
  font-family: inherit;
`

const moodEmoji = ['😢', '😕', '😐', '🙂', '😊']

const labels = {
  BP: { title: 'Log blood pressure', valuePlaceholder: '120/80', unit: 'mmHg' },
  WEIGHT: { title: 'Log weight', valuePlaceholder: '68.4', unit: 'kg' },
  SLEEP_HOURS: { title: 'Log sleep', valuePlaceholder: '7.5', unit: 'hours' },
  MOOD: { title: 'Mood check-in', valuePlaceholder: null, unit: '1-5' },
}

export default function VitalsLogDialog({ open, metric, onClose, onSubmit }) {
  const [valueText, setValueText] = useState('')
  const [moodValue, setMoodValue] = useState(0)
  const [busy, setBusy] = useState(false)

  if (!metric) return null
  const cfg = labels[metric] || { title: 'Log vital', valuePlaceholder: '' }

  function reset() {
    setValueText('')
    setMoodValue(0)
  }

  async function handle() {
    setBusy(true)
    try {
      let body
      if (metric === 'MOOD') {
        body = { metric, value: moodValue, unit: '1-5' }
      } else if (metric === 'BP') {
        const [sys, dia] = valueText.split('/').map((s) => Number(s.trim()))
        body = {
          metric,
          value: sys || null,
          valueText: valueText.trim(),
          unit: 'mmHg',
        }
      } else {
        body = {
          metric,
          value: Number(valueText) || null,
          unit: cfg.unit,
        }
      }
      await onSubmit(body)
      reset()
      onClose()
    } finally {
      setBusy(false)
    }
  }

  const valid = metric === 'MOOD' ? moodValue > 0 : valueText.trim().length > 0

  return (
    <SessionDialog
      open={open}
      title={cfg.title}
      primaryLabel="Save"
      primaryDisabled={!valid}
      primaryLoading={busy}
      onPrimary={handle}
      onClose={() => {
        reset()
        onClose()
      }}
    >
      {metric === 'MOOD' ? (
        <Field>
          <Label>How are you feeling today?</Label>
          <MoodRow>
            {[1, 2, 3, 4, 5].map((n) => (
              <MoodPick
                key={n}
                $on={moodValue === n}
                onClick={() => setMoodValue(n)}
                aria-label={`Mood ${n}`}
              >
                {moodEmoji[n - 1]}
              </MoodPick>
            ))}
          </MoodRow>
        </Field>
      ) : (
        <Field>
          <Label>{cfg.valuePlaceholder ? `Value (${cfg.unit})` : 'Value'}</Label>
          <Input
            value={valueText}
            onChange={(e) => setValueText(e.target.value)}
            placeholder={cfg.valuePlaceholder}
            inputMode={metric === 'BP' ? 'text' : 'decimal'}
          />
        </Field>
      )}
    </SessionDialog>
  )
}
